<template>
  <div class="flex flex-wrap mt-4">
    <div class="w-full mb-12 px-4" v-if="isAdmin">
      <card-bar-chart
        title="Transacciones por fecha (USD)"
        :records="transaccionesPorFecha"
        label="fecha"
        :datasets="chartDatasets"
      />
    </div>
    <div class="w-full mb-12 px-4">
      <card-list
        title="Transacciones"
        resource="transacciones"
        primaryKey="idtransaccion"
        :records="records"
        :meta="meta"
        :columns="columns"
      >
        <template v-slot:actions v-if="isAdmin">
          <button
            class="bg-yellow-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
            type="button"
            @click="exportarCSV()"
          >
            {{ $t("Exportar CSV") }}
          </button>
        </template>
        <template v-slot:after-table>
          <card-list
            title="Totales entre filtros aplicados en USD"
            :records="totalRecords"
            :columns="totalColumns"
            readonly="true"
            hideFilters="true"
            doNotSort="true"
            unableSelect="true"
            hideHeaders="true"
            hideActions="true"
          />
        </template>
      </card-list>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { useToast } from "vue-toastification";
import CardList from "@/components/Cards/CardList.vue";
import CardBarChart from "@/components/Cards/CardBarChart.vue";
import TransaccionService from "@/services/transaccion.service";
import ReporteService from "@/services/reporte.service";
import { transaccion } from "@/models/transaccion";
import DateUtils from "@/utils/dateUtils";
import { hasActiveFilters } from "@/utils/filterUtils";
import { descargarCSV } from "@/utils/descargas";

export default {
  components: {
    CardList,
    CardBarChart,
  },

  data() {
    return {
      records: null,
      meta: {},
      transaccionesPorFecha: [],
      columns: transaccion.filter((column) => column.showOnList),
      totalColumns: [
        {
          name: "label",
          type: "STRING",
        },
        {
          name: "value",
          type: "DECIMAL",
          currency: true,
        },
      ],
    };
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
      startDateFromState: (state) => state.filters.startDate,
      endDateFromState: (state) => state.filters.endDate,
    }),

    isAdmin() {
      return this.userInfo?.roles?.includes("admin");
    },

    queryParams() {
      const params = { ...this.$route.query };
      delete params.startDate;
      delete params.endDate;
      if (this.startDateFromState && this.endDateFromState) {
        params.startDate = this.startDateFromState;
        params.endDate = this.endDateFromState;
      }
      return params;
    },

    chartDatasets() {
      return [
        {
          label: "Cobros",
          backgroundColor: "#86efac",
          borderColor: "#86efac",
          data: this.transaccionesPorFecha.map((r) => r.cobros),
          fill: false,
          barThickness: 8,
        },
        {
          label: "Pagos",
          backgroundColor: "#fca5a5",
          borderColor: "#fca5a5",
          data: this.transaccionesPorFecha.map((r) => r.pagos),
          fill: false,
          barThickness: 8,
        },
      ];
    },

    totalRecords() {
      const ingresos = this.meta.totalIngresos || 0;
      const ivaIngresos = this.meta.totalIVAIngresos || 0;

      const egresos = this.meta.totalEgresos || 0;
      const ivaEgresos = this.meta.totalIVAEgresos || 0;

      const saldo = ingresos - egresos;
      const saldoIVA = ivaEgresos - ivaIngresos;

      return [
        { label: "Ingresos", value: ingresos },
        { label: "Egresos", value: egresos },
        { label: "Saldo", value: saldo || 0 },
        { label: "IVA Ingresos", value: ivaIngresos },
        { label: "IVA Egresos", value: ivaEgresos },
        {
          label: saldoIVA > 0 ? "IVA a favor" : "IVA a pagar",
          value: Math.abs(saldoIVA),
        },
      ];
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler(/*to, from*/) {
        this.loadRecords();

        ["startDateFromState", "endDateFromState"].forEach((property) => {
          this.$watch(
            () => this[property],
            () => {
              const queryParams = new URLSearchParams();
              queryParams.set("page", 1);
              if (this.startDateFromState && this.endDateFromState) {
                queryParams.set("startDate", this.startDateFromState);
                queryParams.set("endDate", this.endDateFromState);
              }
              const queryString = queryParams.toString();
              const url = `/transacciones?${queryString}`;
              this.$router.push(url);
            },
          );
        });
      },
    },
  },

  methods: {
    loadRecords() {
      // undefined = FilterStats aún no inicializó; null = histórico (sin filtro de fechas)
      if (
        this.startDateFromState === undefined ||
        this.endDateFromState === undefined
      ) {
        this.records = [];
        this.meta = {};
        this.transaccionesPorFecha = [];
        return;
      }

      TransaccionService.list(this.queryParams)
        .then((data) => {
          this.records = data.records;
          this.meta = data.meta;
        })
        .catch((response) => {
          console.log(response);
        });

      if (this.isAdmin) {
        ReporteService.transaccionesPorFecha(this.queryParams)
          .then((data) => {
            this.transaccionesPorFecha = data.records;
          })
          .catch((response) => {
            console.log(response);
          });
      }
    },

    exportarCSV() {
      if (
        hasActiveFilters(this.$route.query) &&
        !confirm(this.$t("confirm.exportSelection"))
      ) {
        return;
      }

      const filename = `Transacciones_${DateUtils.formatDate(new Date())}.csv`;
      const toast = useToast();

      TransaccionService.exportarCSV(this.queryParams)
        .then((data) => {
          if (data.csv) {
            descargarCSV(data.csv, filename);
            toast.success("CSV exportado exitosamente");
          } else {
            toast.warning("No hay datos para exportar");
          }
        })
        .catch(() => {
          toast.error("Error al exportar CSV");
        });
    },
  },
};
</script>
